import { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { AlertTriangle, CheckCircle2, ClipboardCheck, Info, X, XCircle } from 'lucide-react'
import { Button, showToast } from '../../../../components'
import { REGULARIZATION_REVIEW_TEXTS } from '../constants/hrmsRegularizationReview.constants'
import type { RegularizationRequest } from '../types/hrmsAttendanceDetails.types'
import type { StaffAttendanceRecord } from '../types/hrmsAttendanceRecords.types'
import { AttendanceComparison } from './AttendanceComparison'
import { RegularizationEmployeeStrip } from './RegularizationEmployeeStrip'
import { RequestSubmissionDetails } from './RequestSubmissionDetails'

interface ReviewRegularizationModalProps {
  isOpen: boolean
  request: RegularizationRequest | null
  record: StaffAttendanceRecord | null
  selectedDate: string
  onClose: () => void
  onApprove: (requestId: string) => Promise<void>
  onReject: (requestId: string) => Promise<void>
}

function getInitials(name: string): string {
  const parts = name.trim().split(' ')
  if (parts.length >= 2) {
    return `${parts[0][0]}${parts[1][0]}`.toUpperCase()
  }
  return name.slice(0, 2).toUpperCase()
}

export function ReviewRegularizationModal({
  isOpen,
  request,
  record,
  selectedDate,
  onClose,
  onApprove,
  onReject,
}: ReviewRegularizationModalProps) {
  const [submitting, setSubmitting] = useState<boolean>(false)
  const [isRejectModalOpen, setIsRejectModalOpen] = useState<boolean>(false)
  const [rejectionReason, setRejectionReason] = useState<string>('')
  const [reasonTouched, setReasonTouched] = useState<boolean>(false)

  const cancelButtonRef = useRef<HTMLButtonElement | null>(null)
  const previousFocusRef = useRef<HTMLElement | null>(null)

  useEffect(() => {
    if (!isOpen) return

    previousFocusRef.current = document.activeElement as HTMLElement

    const timer = setTimeout(() => {
      cancelButtonRef.current?.focus()
    }, 50)

    return () => {
      if (previousFocusRef.current && typeof previousFocusRef.current.focus === 'function') {
        previousFocusRef.current.focus()
      }
      clearTimeout(timer)
    }
  }, [isOpen])

  useEffect(() => {
    if (!isOpen) {
      setIsRejectModalOpen(false)
      setRejectionReason('')
      setReasonTouched(false)
    }
  }, [isOpen])

  useEffect(() => {
    if (!isOpen) return

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !submitting) {
        e.stopPropagation()
        if (isRejectModalOpen) {
          setIsRejectModalOpen(false)
        } else {
          onClose()
        }
      }
    }

    window.addEventListener('keydown', handleKeyDown, true)
    return () => {
      window.removeEventListener('keydown', handleKeyDown, true)
    }
  }, [isOpen, submitting, isRejectModalOpen, onClose])

  if (!isOpen || !request || !record) return null

  const handleApproveAction = async () => {
    setSubmitting(true)
    try {
      await onApprove(request.id)
      showToast({
        type: 'success',
        title: 'Success',
        message: 'Regularization request approved',
      })
      onClose()
    } catch {
      // Error handling
    } finally {
      setSubmitting(false)
    }
  }

  const handleConfirmReject = async () => {
    setReasonTouched(true)
    if (!rejectionReason.trim()) return

    setSubmitting(true)
    try {
      await onReject(request.id)
      showToast({
        type: 'success',
        title: 'Success',
        message: 'Regularization request rejected',
      })
      setIsRejectModalOpen(false)
      onClose()
    } catch {
      // Error handling
    } finally {
      setSubmitting(false)
    }
  }

  const isRejectDisabled = !rejectionReason.trim() || submitting

  return createPortal(
    <>
      {/* Review Regularization Modal */}
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="review-modal-title"
        className="fixed inset-0 z-[60] flex items-center justify-center p-4 overflow-x-hidden"
      >
        {/* Backdrop */}
        <div
          className="fixed inset-0 bg-black/50 backdrop-blur-xs transition-opacity"
          onClick={() => !submitting && onClose()}
        />

        {/* Centred Panel */}
        <div className="relative z-10 w-full max-w-[560px] max-h-[90vh] flex flex-col bg-white rounded-2xl shadow-2xl overflow-hidden">
          {/* Header */}
          <div className="bg-white border-b border-slate-200 p-5 flex items-start justify-between shrink-0 gap-3">
            <div className="flex items-start gap-2.5 min-w-0">
              <ClipboardCheck className="h-5 w-5 text-[#146971] shrink-0 mt-0.5" />
              <div className="min-w-0">
                <h2 id="review-modal-title" className="text-lg font-bold text-slate-900 truncate">
                  {REGULARIZATION_REVIEW_TEXTS.title}
                </h2>
                <p className="text-xs text-slate-500 mt-0.5">
                  {REGULARIZATION_REVIEW_TEXTS.subtitle}
                </p>
              </div>
            </div>

            <Button
              variant="secondary"
              size="sm"
              aria-label="Close"
              disabled={submitting}
              icon={<X className="h-4 w-4" />}
              onClick={onClose}
              className="h-8 w-8 p-0 flex items-center justify-center border-none bg-transparent hover:bg-slate-100 text-slate-500 rounded-lg shrink-0"
            />
          </div>

          {/* Scrollable Body */}
          <div className="flex-1 overflow-y-auto p-5 space-y-4">
            <RegularizationEmployeeStrip
              name={record.name}
              designation={record.designation}
              employeeId={record.employeeId}
              department={record.department}
              selectedDate={selectedDate}
            />

            <AttendanceComparison
              currentCheckIn={request.currentCheckIn}
              currentCheckOut={request.currentCheckOut}
              requestedCheckIn={request.requestedCheckIn}
              requestedCheckOut={request.requestedCheckOut}
              currentSource={record.source}
            />

            <RequestSubmissionDetails
              reason={request.reason}
              requestedAt={request.requestedAt}
              status={request.status}
            />
          </div>

          {/* Sticky Footer */}
          <div className="bg-white border-t border-slate-200 p-5 flex items-center justify-between gap-3 shrink-0">
            <Button
              ref={cancelButtonRef}
              variant="secondary"
              size="md"
              disabled={submitting}
              onClick={onClose}
              className="border-none bg-transparent hover:bg-slate-100 text-slate-600 font-semibold"
            >
              Cancel
            </Button>

            <div className="flex items-center gap-2.5">
              <Button
                variant="danger"
                size="md"
                disabled={submitting}
                icon={<XCircle className="h-4 w-4" />}
                onClick={() => setIsRejectModalOpen(true)}
                className="font-semibold justify-center"
              >
                Reject
              </Button>

              <Button
                variant="primary"
                size="md"
                disabled={submitting}
                isLoading={submitting}
                icon={<CheckCircle2 className="h-4 w-4" />}
                onClick={handleApproveAction}
                className="font-semibold justify-center bg-[#0F766E] hover:bg-[#0d645e] text-white"
              >
                Approve
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Reject Regularization Request Modal */}
      {isRejectModalOpen && (
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="reject-modal-title"
          className="fixed inset-0 z-[70] flex items-center justify-center p-4 overflow-x-hidden"
        >
          {/* Backdrop */}
          <div
            className="fixed inset-0 bg-black/50 transition-opacity"
            onClick={() => !submitting && setIsRejectModalOpen(false)}
          />

          {/* Reject Modal Box */}
          <div className="relative z-10 w-full max-w-[520px] max-h-[90vh] flex flex-col bg-white rounded-2xl shadow-2xl overflow-hidden">
            {/* Header */}
            <div className="bg-gray-50 border-b border-gray-200 p-5 flex items-start justify-between shrink-0 gap-3">
              <div className="flex items-start gap-2.5 min-w-0">
                <AlertTriangle className="h-5 w-5 text-red-600 shrink-0 mt-0.5" />
                <div className="min-w-0">
                  <h2 id="reject-modal-title" className="text-lg font-bold text-slate-900 truncate">
                    Reject Regularization Request
                  </h2>
                  <p className="text-xs text-gray-500 mt-0.5">
                    Please provide a reason for rejecting this attendance correction request.
                  </p>
                </div>
              </div>

              <button
                type="button"
                aria-label="Close dialog"
                disabled={submitting}
                onClick={() => setIsRejectModalOpen(false)}
                className="rounded-full p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-200 transition cursor-pointer shrink-0"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            {/* Body */}
            <div className="p-6 space-y-5 overflow-y-auto flex-1">
              {/* Employee Card */}
              <div className="rounded-xl border border-gray-200 bg-gray-50 p-4 flex items-center justify-between min-w-0 gap-3">
                <div className="flex items-center gap-3 min-w-0">
                  <div className="h-10 w-10 rounded-full bg-[#0F766E] text-white font-bold flex items-center justify-center shrink-0 text-sm">
                    {getInitials(record.name)}
                  </div>
                  <div className="min-w-0">
                    <p className="font-bold text-slate-900 truncate text-sm">{record.name}</p>
                    <p className="text-xs text-slate-500 truncate mt-0.5">
                      <span className="font-semibold text-[#0F766E]">{record.employeeId}</span> • {selectedDate}
                    </p>
                  </div>
                </div>

                <div className="shrink-0">
                  <span className="bg-amber-50 text-amber-800 border border-amber-200 text-xs font-semibold px-2.5 py-1 rounded-full inline-flex items-center gap-1.5">
                    <span className="h-1.5 w-1.5 rounded-full bg-amber-500" />
                    Pending Approval
                  </span>
                </div>
              </div>

              {/* Textarea Section */}
              <div>
                <label className="block text-sm font-semibold text-slate-900 mb-1">
                  Reason for Rejection <span className="text-red-500">*</span>
                </label>
                <textarea
                  value={rejectionReason}
                  onChange={(e) => {
                    setRejectionReason(e.target.value)
                    setReasonTouched(true)
                  }}
                  placeholder="Enter reason for rejecting this request..."
                  rows={4}
                  className={`min-h-[100px] w-full rounded-xl bg-gray-100 border p-3 text-sm text-slate-900 focus:bg-white focus:outline-none focus:ring-2 focus:ring-[#0F766E] focus:border-transparent resize-none ${
                    reasonTouched && !rejectionReason.trim()
                      ? 'border-red-400 bg-red-50/50'
                      : 'border-gray-200'
                  }`}
                />
                {reasonTouched && !rejectionReason.trim() && (
                  <p className="text-xs text-red-600 font-medium mt-1">
                    Rejection reason is required.
                  </p>
                )}
              </div>

              {/* Info Note */}
              <div className="rounded-lg border border-gray-200 bg-gray-50 p-3 flex items-start gap-2.5 text-xs text-slate-600">
                <Info className="h-4 w-4 text-slate-500 shrink-0 mt-0.5" />
                <p>
                  The rejection reason will be recorded with the attendance request and will be visible to the employee.
                </p>
              </div>
            </div>

            {/* Footer */}
            <div className="bg-gray-50 border-t border-gray-200 px-6 py-4 flex items-center justify-end gap-3 shrink-0">
              <Button
                variant="secondary"
                size="md"
                disabled={submitting}
                onClick={() => setIsRejectModalOpen(false)}
                className="border-none bg-transparent hover:bg-gray-200 text-slate-600 font-semibold"
              >
                Cancel
              </Button>

              <button
                type="button"
                disabled={isRejectDisabled}
                onClick={handleConfirmReject}
                className="bg-red-700 hover:bg-red-800 disabled:opacity-50 text-white font-semibold rounded-lg px-5 py-2.5 text-sm inline-flex items-center gap-2 transition cursor-pointer"
              >
                <XCircle className="h-4 w-4" />
                {submitting ? 'Rejecting...' : 'Confirm Rejection'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>,
    document.body
  )
}

